<template>
  <div class="custom-doc-upload">
    <!-- 文档上传/预览UI（只做展示） -->
    <div class="upload-area" @click="handleClickUpload" v-if="!docName">
      <div class="upload-placeholder">
        <custom-icon type="shangchuan"></custom-icon>
        <p>点击上传文档</p>
        <p class="tip">支持PDF/WORD/EXCEL/ZIP，大小不超过10MB</p>
      </div>
    </div>

    <div class="preview-area" v-else>
      <div class="doc-info">
        <n-icon size="18" color="#409eff"><Document /></n-icon>
        <span class="doc-name text-ellipsis">{{ docName }}</span>
        <span class="doc-size">{{ docSize }}</span>
      </div>
      <div class="preview-actions">
        <n-button
          :disabled="!isEdit"
          size="small"
          type="primary"
          @click="handleClickUpload"
          >重新上传</n-button
        >
        <n-button
          size="small"
          type="error"
          :disabled="!isEdit"
          @click="handleRemove"
          >删除</n-button
        >
      </div>
    </div>

    <!-- 隐藏的n-upload（逻辑交给upload.ts） -->
    <n-upload
      ref="uploadRef"
      v-bind="$attrs"
      :custom-request="handleCustomRequest"
      :before-upload="handleBeforeUpload"
      :accept="accept"
      :multiple="multiple"
      style="display: none"
    />
    <upload-progress :loading="uploadLoading" :progress="uploadProgress" />
  </div>
</template>

<script setup lang="ts">
import { useFileStore } from '@/store/file/file'
import type { UploadFileInfo } from 'naive-ui'
import { computed, ref, watch } from 'vue'
import UploadProgress from './UploadProgress.vue'
import { formatMaxFileSize } from './utils' // 复用大小格式化方法
import { createUploadLogic } from './utils/upload'

// 1. 接收props（和图片组件对齐）
const props = defineProps<{
  modelValue: any
  fileKey?: string | undefined
  isEdit?: boolean
  scene?: string | undefined
  uploadBtnTitle?: string | undefined
  maxFileSize?: number | undefined
  accept?: string | undefined
  isFileUrl?: boolean | undefined
  isFileId?: boolean | undefined
  multiple?: boolean | undefined
}>()

// 2. 定义事件（和图片组件对齐）
const emit = defineEmits<{
  'update:modelValue': [value: any]
  'upload-progress': [progress: number]
  success: [fileData: any]
  error: [file: UploadFileInfo]
  remove: [file: UploadFileInfo]
}>()

// 3. 初始化upload逻辑（强制文档类型）
const fileStore = useFileStore()
const uploadRef = ref()
const uploadLoading = computed(() =>
  fileStore.getUploadLoadingByKey(props.fileKey)
)

const uploadLogic = createUploadLogic(
  { ...props, multiple: false, uploadType: 'file' }, // 强制文档类型
  emit,
  fileStore
)
const uploadProgress = uploadLogic.uploadProgress
// 4. 同步文档状态（从uploadLogic推导）
const currentFile = computed(() => uploadLogic.getCurrentFile())
const docName = computed(
  () => currentFile.value?.name || props.modelValue || ''
)
const docSize = computed(() => {
  return currentFile.value ? formatMaxFileSize(currentFile.value.size) : ''
})

// 5. 绑定核心方法（复用uploadLogic）
const handleBeforeUpload = (options: { file: UploadFileInfo }) => {
  return uploadLogic.handleBeforeUpload(options)
}

const handleCustomRequest = (options: any) => {
  return uploadLogic.handleCustomRequest(options)
}

const handleClickUpload = () => {
  uploadLogic.triggerUpload(uploadRef.value)
}

const handleRemove = async () => {
  const file = currentFile.value
  if (file) {
    const uploadFileInfo: UploadFileInfo = {
      id: file.id,
      name: file.name,
      status: 'finished',
      url: file.url
    }
    await uploadLogic.handleRemove({ file: uploadFileInfo })
  }
}

// 6. 监听进度（透传父组件）
watch(uploadLogic.uploadProgress, (progress) => {
  emit('upload-progress', progress)
})

// 暴露方法
defineExpose({
  uploadRef,
  clearUpload: uploadLogic.handleClear,
  getFileList: () => uploadLogic.internalFileList.value
})
</script>

<style scoped lang="scss">
.custom-doc-upload {
  width: 100%;
  flex: 1;
  .upload-area {
    height: 120px;
    border: 1px dashed #d9d9d9;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover {
      border-color: $primary-color;
    }
    .tip {
      font-size: 12px;
      margin-top: 8px;
    }
    .upload-placeholder {
      text-align: center;
      color: #909399;
    }
  }
  .preview-area {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    .doc-info {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px;
      background: #f9fafc;
    }
    .doc-name {
      flex: 1;
      font-size: 14px;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .doc-size {
      font-size: 12px;
      color: #909399;
    }
    .preview-actions {
      display: flex;
      gap: 8px;
      padding: 8px 12px;
      background: #f5f7fa;
    }
  }
}
</style>
